import { useCallback, useEffect, useRef, useState } from "react";
import {
  FilterConfig,
  applyLiveNoise,
  buildInputFilterChain,
  buildOutputFilterChain,
} from "../../utils/telephonyFilters";

/**
 * Phone-call simulator audio hook.
 *
 * Wraps a Web Audio graph (defined in `utils/telephonyFilters.ts`) that
 * mimics what voice goes through on a real mobile/PSTN call:
 *
 *   mic → bandpass(300–3400) → AGC → µ-law roundtrip → +noise → resample 8k → server
 *   server → 16k buffer → bandpass(300–3400) → µ-law roundtrip → +noise → speaker
 *
 * Filter settings are captured at connect() time, except for noise (toggle
 * + SNR), which is reactive thanks to `applyLiveNoise`.
 */

// Re-exported so existing imports from this module keep working.
export type { FilterConfig, FilterMode } from "../../utils/telephonyFilters";
export { profileToConfig } from "../../utils/telephonyFilters";

export interface PhoneCallEvent {
  ts: number;
  level: "info" | "warn" | "error";
  text: string;
}

export interface UsePhoneCallOptions {
  language?: string;
  useCase?: string;
  filterConfig?: FilterConfig;
  /**
   * PCM rate sent to the server (mic uplink). 8 kHz mirrors a real
   * telephony uplink and is what the STT stack should be tested with.
   */
  inputSampleRate?: number;
  /**
   * PCM rate requested from the server for the bot's audio (downlink).
   * Defaults to 16 kHz because the TTS cache and most providers run at
   * 16 kHz natively — picking 8 kHz here can trigger an internal rate
   * mismatch (cached frames are 16 kHz; if LiveKit's resampler isn't
   * primed before the first frame, the bytes ship at 16 kHz but get
   * labeled at the 8 kHz output rate, which the browser then plays
   * back at 2× speed). The phone-call character on output comes from
   * the client-side bandpass + µ-law chain, not the source rate.
   */
  outputSampleRate?: number;
  onEvent?: (ev: PhoneCallEvent) => void;
}

// ── Telephony constants ──────────────────────────────────────────────

const TELEPHONY_INPUT_RATE = 8000;   // mic uplink — narrowband telephony
const TELEPHONY_OUTPUT_RATE = 16000; // server downlink — see UsePhoneCallOptions

// ── Audio helpers ────────────────────────────────────────────────────

function resample(input: Float32Array, inRate: number, outRate: number): Float32Array {
  if (inRate === outRate) return input;
  const ratio = inRate / outRate;
  const outLen = Math.round(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcIdx = i * ratio;
    const idx = Math.floor(srcIdx);
    const frac = srcIdx - idx;
    out[i] =
      idx + 1 < input.length
        ? input[idx] * (1 - frac) + input[idx + 1] * frac
        : input[idx];
  }
  return out;
}

function float32ToPcm16(f32: Float32Array): Int16Array {
  const out = new Int16Array(f32.length);
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function pcm16ToFloat32(pcm: Int16Array): Float32Array {
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = pcm[i] / 0x8000;
  return out;
}

// ── Backend URL ──────────────────────────────────────────────────────

function backendWsUrl(
  language: string,
  useCase: string,
  clientId: string,
  inRate: number,
  outRate: number
): string {
  let backendUrl: string = (import.meta as any).env?.VITE_BACKEND_URL ?? "";
  if (
    backendUrl &&
    backendUrl.includes("localhost") &&
    !window.location.hostname.includes("localhost")
  ) {
    backendUrl = window.location.origin;
  }
  if (!backendUrl) backendUrl = window.location.origin;
  const base = backendUrl.replace(/^https?/, (p) => (p === "https" ? "wss" : "ws"));
  const params = new URLSearchParams({
    client_id: clientId,
    language,
    use_case: useCase,
    input_audio_sample_rate: String(inRate),
    output_audio_sample_rate: String(outRate),
  });
  return `${base}/v1/voice-bot/ws?${params.toString()}`;
}

// ── Hook ─────────────────────────────────────────────────────────────

export function usePhoneCall(options: UsePhoneCallOptions = {}) {
  const {
    language = "vi",
    useCase = "ha_scenario",
    filterConfig,
    inputSampleRate = TELEPHONY_INPUT_RATE,
    outputSampleRate = TELEPHONY_OUTPUT_RATE,
    onEvent,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [clientId, setClientId] = useState("");
  const [micLevel, setMicLevel] = useState(0);
  const [transcripts, setTranscripts] = useState<
    { speaker: "user" | "bot"; text: string; ts: number }[]
  >([]);

  // ── Refs ──
  const wsRef = useRef<WebSocket | null>(null);
  const micCtxRef = useRef<AudioContext | null>(null);
  const playCtxRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const micProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const micNoiseSrcRef = useRef<AudioBufferSourceNode | null>(null);
  const micNoiseGainRef = useRef<GainNode | null>(null);
  const playNoiseSrcRef = useRef<AudioBufferSourceNode | null>(null);
  const playNoiseGainRef = useRef<GainNode | null>(null);
  const playChainEntryRef = useRef<AudioNode | null>(null);
  const playNextStartRef = useRef<number>(0);
  // Active bot-audio sources scheduled into the playback chain. Tracked
  // so we can call .stop() on every one when the server signals an
  // interrupt (`response.audio.clear`) or false-interrupt pause
  // (`response.audio.paused`) — without this, chunks already scheduled
  // before the interrupt keep playing for up to ~200 ms after the bot
  // was supposed to go silent.
  const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());

  const stopAllChunks = useCallback(() => {
    for (const src of activeSourcesRef.current) {
      try {
        src.onended = null;
        src.stop();
      } catch {
        /* may already have ended */
      }
      try {
        src.disconnect();
      } catch {
        /* ignore */
      }
    }
    activeSourcesRef.current.clear();
    playNextStartRef.current = playCtxRef.current?.currentTime ?? 0;
  }, []);

  const filterRef = useRef<FilterConfig | null>(filterConfig ?? null);

  // Keep the noise gain in sync with the live filter config. Other
  // chain settings (bandpass, µ-law, sides) require rebuilding the
  // Web Audio graph, so they're only honoured at connect() time.
  // Noise is just a gain ramp, so we can apply it instantly without
  // disturbing the audio flow — the noise toggle and SNR slider are
  // therefore live during a call.
  useEffect(() => {
    filterRef.current = filterConfig ?? null;
    const micCtx = micCtxRef.current;
    const playCtx = playCtxRef.current;
    if (micCtx)
      applyLiveNoise(micCtx, micNoiseGainRef.current, filterConfig ?? null, "input");
    if (playCtx)
      applyLiveNoise(playCtx, playNoiseGainRef.current, filterConfig ?? null, "output");
  }, [filterConfig]);

  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const emit = useCallback(
    (level: PhoneCallEvent["level"], text: string) => {
      onEventRef.current?.({ ts: Date.now(), level, text });
    },
    []
  );

  // ── Schedule one PCM chunk into the playback chain ──
  const playChunk = useCallback((pcm: Int16Array, sampleRate: number) => {
    const ctx = playCtxRef.current;
    const entry = playChainEntryRef.current;
    if (!ctx || !entry || pcm.length === 0) return;

    // Some browsers leave the context in 'suspended' state until a
    // gesture-driven resume(). connect() is called from a button click
    // but with awaits in between, so kick it explicitly each chunk —
    // resume() on an already-running context is a no-op.
    if (ctx.state === "suspended") void ctx.resume();

    const buf = ctx.createBuffer(1, pcm.length, sampleRate);
    buf.getChannelData(0).set(pcm16ToFloat32(pcm));
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(entry);

    const now = ctx.currentTime;
    const startAt = Math.max(now, playNextStartRef.current);
    src.start(startAt);
    playNextStartRef.current = startAt + buf.duration;

    activeSourcesRef.current.add(src);
    src.onended = () => {
      activeSourcesRef.current.delete(src);
      try {
        src.disconnect();
      } catch {
        /* ignore */
      }
    };
  }, []);

  // ── Connect ──
  const connect = useCallback(async () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const cfg = filterRef.current;
    const newClientId = Math.random().toString(36).substring(2, 18);
    setClientId(newClientId);
    emit("info", `Connecting (client_id=${newClientId}, profile=${describeProfile(cfg)})`);

    // 1) Mic stream — keep browser AEC + NS + AGC ON. AEC is required
    //    when the user is on speakers so the bot's audio doesn't loop
    //    back through the mic and get re-sent to the server (which
    //    would then transcribe it as user speech). NS + AGC mirror the
    //    pre-processing every real handset applies before transmit.
    //    Real telephony also has built-in echo cancellation, so leaving
    //    it on doesn't break the "phone-call" character.
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (e) {
      emit("error", `getUserMedia failed: ${(e as Error).message}`);
      throw e;
    }
    micStreamRef.current = stream;

    // 2) Mic AudioContext at native rate — bandpass before resample
    //    avoids aliasing when we drop down to 8 kHz.
    const micCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    micCtxRef.current = micCtx;
    const nativeRate = micCtx.sampleRate;

    const source = micCtx.createMediaStreamSource(stream);
    const inputChain = buildInputFilterChain(micCtx, cfg);
    micNoiseSrcRef.current = inputChain.noiseSource;
    micNoiseGainRef.current = inputChain.noiseGain;
    source.connect(inputChain.entry);
    const chainExit = inputChain.exit;

    const processor = micCtx.createScriptProcessor(4096, 1, 1);
    micProcessorRef.current = processor;
    chainExit.connect(processor);
    // Chrome quirk: ScriptProcessor needs to terminate at destination.
    // Route via a muted gain so the user doesn't hear their own mic.
    const sink = micCtx.createGain();
    sink.gain.value = 0;
    processor.connect(sink).connect(micCtx.destination);

    let levelDecay = 0;
    processor.onaudioprocess = (e) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);

      // Mic level for UI (peak with simple decay)
      let peak = 0;
      for (let i = 0; i < input.length; i++) {
        const a = Math.abs(input[i]);
        if (a > peak) peak = a;
      }
      levelDecay = Math.max(peak, levelDecay * 0.85);
      setMicLevel(levelDecay);

      const resampled = resample(input, nativeRate, inputSampleRate);
      const pcm = float32ToPcm16(resampled);
      ws.send(pcm.buffer);
    };

    // 3) Playback AudioContext (separate so it can run at its own rate)
    const playCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    playCtxRef.current = playCtx;
    const outputChain = buildOutputFilterChain(playCtx, cfg);
    playNoiseSrcRef.current = outputChain.noiseSource;
    playNoiseGainRef.current = outputChain.noiseGain;
    playChainEntryRef.current = outputChain.entry;
    playNextStartRef.current = playCtx.currentTime;
    // Browsers create AudioContext suspended until a user gesture
    // resumes it; the gesture chain may be broken by getUserMedia's
    // await above, so resume explicitly. No-op if already running.
    if (playCtx.state === "suspended") {
      try {
        await playCtx.resume();
      } catch {
        /* ignore */
      }
    }

    // 4) WebSocket
    const wsUrl = backendWsUrl(
      language,
      useCase,
      newClientId,
      inputSampleRate,
      outputSampleRate
    );
    emit("info", `WS URL: ${wsUrl}`);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      emit("info", "WebSocket open");
      try {
        ws.send(JSON.stringify({ event_type: "conversation.start", data: {} }));
      } catch (err) {
        emit("warn", `conversation.start send failed: ${(err as Error).message}`);
      }
    };

    let firstDeltaLogged = false;

    ws.onmessage = (event) => {
      // Backend (ws_io.py) sends every audio frame as a JSON event
      // {"type": "response.audio.delta", "delta": "<base64 PCM16>",
      //  "sequence": N, "sample_rate": <rate>}. Binary frames are kept
      // for forward-compat (some servers stream raw PCM).
      if (event.data instanceof ArrayBuffer) {
        playChunk(new Int16Array(event.data), outputSampleRate);
      } else if (event.data instanceof Blob) {
        event.data
          .arrayBuffer()
          .then((ab) => playChunk(new Int16Array(ab), outputSampleRate));
      } else if (typeof event.data === "string") {
        const text = event.data;
        for (const line of text.split("\n")) {
          const json = line.startsWith("data: ") ? line.slice(6) : line;
          if (!json.trim().startsWith("{")) continue;
          try {
            handleStreamEvent(JSON.parse(json));
          } catch {
            /* ignore non-JSON */
          }
        }
      }
    };

    ws.onerror = () => {
      emit("error", "WebSocket error");
    };

    ws.onclose = (ev) => {
      emit("info", `WebSocket closed (code=${ev.code} reason="${ev.reason}")`);
      setIsConnected(false);
    };

    function handleStreamEvent(msg: any) {
      const t = msg?.type;
      if (t === "response.audio.delta") {
        const b64 = msg.delta;
        if (typeof b64 === "string" && b64.length > 0) {
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const sr =
            typeof msg.sample_rate === "number" && msg.sample_rate > 0
              ? msg.sample_rate
              : outputSampleRate;
          if (!firstDeltaLogged) {
            firstDeltaLogged = true;
            const samples = bytes.byteLength / 2;
            const durationMs = Math.round((samples / sr) * 1000);
            emit(
              "info",
              `First audio.delta: server sample_rate=${sr} ` +
                `(requested=${outputSampleRate}), bytes=${bytes.byteLength}, ` +
                `samples=${samples}, chunk=${durationMs}ms`
            );
            if (sr !== outputSampleRate) {
              emit(
                "warn",
                `Server returned a different sample_rate than requested — ` +
                  `playback follows the server's value, so audio speed is correct ` +
                  `as long as the server is honest about its rate.`
              );
            }
          }
          playChunk(new Int16Array(bytes.buffer), sr);
        }
      } else if (t === "response.audio.clear") {
        // Hard interrupt: server cleared its buffer (real barge-in
        // after min_words). Cut every queued + currently-playing
        // chunk so the bot goes silent immediately.
        emit("info", "Server interrupted bot (response.audio.clear)");
        stopAllChunks();
      } else if (t === "response.audio.paused") {
        // Soft interrupt: server paused waiting to see if the user
        // really has something to say (false_interruption_timeout).
        // Same client-side action — stop audible playback. If the
        // user goes silent, server resumes via response.audio.resumed
        // and starts sending fresh chunks.
        emit("info", "Bot paused (false-interrupt detected)");
        stopAllChunks();
      } else if (t === "response.audio.resumed") {
        emit("info", "Bot resumed (false-interrupt expired)");
      } else if (t === "response.audio.done") {
        // Server finished streaming this turn — nothing to do; the
        // browser keeps draining queued AudioBufferSources.
      } else if (t === "session.closed") {
        emit("info", `Session closed by server: ${msg.reason ?? ""}`);
      } else if (t === "input_audio_buffer.transcribed" || t === "user_transcript") {
        const text = msg.transcript ?? msg.text;
        if (text) {
          setTranscripts((prev) => [
            ...prev,
            { speaker: "user", text, ts: Date.now() },
          ]);
        }
      } else if (t === "response.text.delta" || t === "response.output_text.delta") {
        const text = msg.delta ?? msg.text;
        if (text) {
          setTranscripts((prev) => [
            ...prev,
            { speaker: "bot", text, ts: Date.now() },
          ]);
        }
      }
    }
  }, [emit, inputSampleRate, language, outputSampleRate, playChunk, useCase]);

  // ── Disconnect ──
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.close(1000, "client_disconnect");
      } catch {
        /* ignore */
      }
      wsRef.current = null;
    }
    if (micProcessorRef.current) {
      try {
        micProcessorRef.current.onaudioprocess = null;
        micProcessorRef.current.disconnect();
      } catch {
        /* ignore */
      }
      micProcessorRef.current = null;
    }
    if (micNoiseSrcRef.current) {
      try {
        micNoiseSrcRef.current.stop();
      } catch {
        /* ignore */
      }
      micNoiseSrcRef.current = null;
    }
    if (playNoiseSrcRef.current) {
      try {
        playNoiseSrcRef.current.stop();
      } catch {
        /* ignore */
      }
      playNoiseSrcRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (micCtxRef.current) {
      try {
        micCtxRef.current.close();
      } catch {
        /* ignore */
      }
      micCtxRef.current = null;
    }
    if (playCtxRef.current) {
      try {
        playCtxRef.current.close();
      } catch {
        /* ignore */
      }
      playCtxRef.current = null;
    }
    stopAllChunks();
    playChainEntryRef.current = null;
    playNextStartRef.current = 0;
    setMicLevel(0);
    setIsConnected(false);
    emit("info", "Disconnected");
  }, [emit]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    connect,
    disconnect,
    isConnected,
    clientId,
    micLevel,
    transcripts,
  };
}

// ── Helpers used by the page ─────────────────────────────────────────

function describeProfile(cfg: FilterConfig | null): string {
  if (!cfg) return "off";
  const parts: string[] = [];
  if (cfg.bandpass) parts.push("bandpass");
  if (cfg.ulaw) parts.push("µ-law");
  if (cfg.noise) parts.push(`noise@${cfg.noiseSnrDb}dB`);
  if (parts.length === 0) return "off";
  const sides = [
    cfg.applyOnInput ? "in" : null,
    cfg.applyOnOutput ? "out" : null,
  ]
    .filter(Boolean)
    .join("+");
  return `${parts.join(",")} (${sides})`;
}

